import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Download, RefreshCw, ScrollText } from "lucide-react";
import {
  ActionMenu,
  Alert,
  Button,
  Card,
  ErrorPage,
  PageContent,
  PageHeader,
  PageShell,
  PageSkeleton,
  Tabs,
} from "@h2j-data-team/ui";

import { toast } from "@/components/shared";
import { BackLink } from "@/components/back-link";
import { supportCode } from "@/components/error-screen";
import { ApiError, getErrorMessage } from "@/lib/api-client";
import { downloadQvdExport } from "@/lib/api/qvd-analysis";
import { RETRIABLE_QVD_REASONS } from "@repo/shared";
import { connectionQueryOptions } from "@/features/qlik-connections/api";
import {
  qvdAnalysisRunQueryOptions,
  qvdAnalysisKeys,
  isRunActive,
  useCancelQvdAnalysis,
  useReprocessQvdAnalysis,
  useResumeQvdAnalysis,
  useStartQvdAnalysis,
} from "@/features/qvd-analysis/api";
import {
  QvdAnalysisStatusStrip,
  QvdCoverageBanner,
  QvdSizeCoverageBanner,
  QvdKpiCells,
  NotAnalyzableSection,
  UnverifiedAppsNotice,
  OrphanQvdsTable,
  UnloadedColumnsTable,
  EvidenceIncompleteTable,
  QvdFootprintBySpace,
  QvdFootprintByType,
  QvdFilesTable,
  QvdRunLogsDrawer,
  QvdScopeBadges,
  CompareQvdRunMenu,
} from "@/components/qvd-analysis";

type Tab = "orphans" | "columns" | "evidence" | "bySpace" | "files";

export const Route = createFileRoute("/_authenticated/qvd-analyses/$runId")({
  validateSearch: (search: Record<string, unknown>): { tab: Tab } => ({
    tab:
      search.tab === "columns" ||
      search.tab === "evidence" ||
      search.tab === "bySpace" ||
      search.tab === "files"
        ? search.tab
        : "orphans",
  }),
  component: QvdAnalysisDetailPage,
});

function QvdAnalysisDetailPage() {
  const { t } = useTranslation("qvd-analysis");
  const { t: tc } = useTranslation("common");
  const { runId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data: run, isLoading, error, refetch } = useQuery(qvdAnalysisRunQueryOptions(runId));
  const { data: connection } = useQuery({
    ...connectionQueryOptions(run?.connectionId ?? ""),
    enabled: !!run?.connectionId,
  });
  const cancelMutation = useCancelQvdAnalysis();
  const reprocessMutation = useReprocessQvdAnalysis();
  const resumeMutation = useResumeQvdAnalysis();
  const startMutation = useStartQvdAnalysis();
  const queryClient = useQueryClient();
  const [logsOpen, setLogsOpen] = useState(false);
  const logsButton = useRef<HTMLButtonElement>(null);

  const notAnalyzable = run?.coverage?.notAnalyzable ?? [];
  const retriableReasons = new Set<string>(RETRIABLE_QVD_REASONS);
  const retriableCount = notAnalyzable.filter((x) => retriableReasons.has(x.reason)).length;

  // The run polls (2s) while active, refreshing coverage/KPIs. Mirror that into
  // the result tables: whenever the orphan/column counts or the status change,
  // invalidate their queries so newly-found rows appear live — not only after a
  // manual page refresh. Counts are stable once the run is idle, so this stops
  // firing when there is nothing new.
  const orphanCount = run?.coverage?.orphanCount;
  const unloadedCount = run?.coverage?.unloadedColumnQvdCount;
  // The verdict-withheld population grows independently of unloadedCount (its
  // rows have zero not-loaded columns), so it needs its own trigger — the
  // columns key is the prefix of both lists, so one invalidation covers them.
  const evidenceCount = run?.coverage?.evidenceIncompleteCount;
  const runStatus = run?.status;
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: qvdAnalysisKeys.orphans(runId) });
    queryClient.invalidateQueries({ queryKey: qvdAnalysisKeys.columns(runId) });
  }, [runId, orphanCount, unloadedCount, evidenceCount, runStatus, queryClient]);

  const setTab = (next: Tab) => navigate({ search: (prev) => ({ ...prev, tab: next }) });

  const handleExport = (type: "orphans" | "columns", format: "csv" | "json") => {
    if (!run) return;
    downloadQvdExport(run.id, type, format).catch((err) =>
      toast.error(getErrorMessage(err, t("toast.exportFailed"))),
    );
  };

  // Re-analyze only the retriable not-analyzable QVDs and merge the results back
  // into THIS run (no new run) — stay on the page; the run re-enters processing.
  const handleReprocess = () => {
    if (!run || retriableCount === 0) return;
    reprocessMutation.mutate(run.id, {
      onSuccess: () => toast.success(t("toast.reprocessStarted")),
      onError: (err) => toast.error(getErrorMessage(err, t("toast.startFailed"))),
    });
  };

  // Continue the QVDs a stopped run never reached ("not_processed", e.g. a run
  // that hit the safety timeout) and merge the results back into THIS run — the
  // worker derives the unreached subset itself, so only they are re-analyzed.
  const handleResume = () => {
    if (!run) return;
    resumeMutation.mutate(run.id, {
      onSuccess: () => toast.success(t("toast.resumeStarted")),
      onError: (err) => toast.error(getErrorMessage(err, t("toast.startFailed"))),
    });
  };

  // Footprint-only runs can't be "upgraded" in place — start a fresh full
  // analysis for the same connection + scope and jump to it.
  const handleAnalyzeOrphans = () => {
    if (!run) return;
    startMutation.mutate(
      {
        connectionId: run.connectionId,
        scope: {
          spaceIds: run.scope.spaceIds,
          qvdIds: [],
          namePatterns: run.scope.namePatterns,
          spaces: run.scope.spaces,
        },
        forceFullRefresh: false,
        evaluateTotalSpace: true,
        evaluateOrphans: true,
        evaluateUnusedColumns: true,
      },
      {
        onSuccess: ({ run: newRun }) =>
          navigate({
            to: "/qvd-analyses/$runId",
            params: { runId: newRun.id },
            search: { tab: "orphans" },
          }),
        onError: (err) => toast.error(getErrorMessage(err, t("toast.startFailed"))),
      },
    );
  };

  const back = <BackLink to="/qvd-analyses" label={t("detail.back")} />;

  if (isLoading) return <PageSkeleton variant="detail" label={tc("list.loading")} />;

  if (error || !run) {
    const header = <PageHeader title={t("title")} back={back} />;
    // A missing run (deleted, or another organization's) is not a failure to
    // retry; anything else is, and says nothing of the server's own message.
    if (error instanceof ApiError && error.status === 404) {
      return (
        <ErrorPage
          kind="not-found"
          header={header}
          title={t("detail.notFound.title")}
          description={t("detail.notFound.description")}
        />
      );
    }
    return (
      <ErrorPage
        kind="load-failed"
        header={header}
        title={t("detail.loadFailed.title")}
        description={t("detail.loadFailed.description")}
        supportCode={supportCode(error, tc("error.supportCode"))}
        actions={
          <Button variant="outline" onClick={() => void refetch()}>
            <RefreshCw />
            {tc("tryAgain")}
          </Button>
        }
      />
    );
  }

  // Which result surfaces to show, driven by what the run evaluated. Footprint
  // is data-gated (a run flagged for it but with no files yields nothing).
  const hasFootprint = !!run.coverage && run.coverage.footprintFileCount > 0;
  // "Size by space" shows whenever there's a per-space breakdown — even for a
  // single space, since its row drills into the directory tree.
  const hasSpaceBreakdown = !!run.coverage && run.coverage.footprintBySpace.length > 0;
  const availableTabs: Tab[] = [];
  if (run.evaluateOrphans) availableTabs.push("orphans");
  if (run.evaluateUnusedColumns) availableTabs.push("columns");
  // The QVDs left without a verdict get their own tab instead of polluting the
  // unloaded-columns list. Only shown when the run actually produced some —
  // an empty tab would be noise on a clean run.
  //
  // Visibility and the badge stay on the COVERAGE counter, not on the fetched
  // rows: the list is paginated (rows.length is one page, not the total) and
  // deriving visibility from it would flicker the tab in after load and break a
  // ?tab=evidence deep link. The two can disagree — the counter is saved after
  // the rows, so a run cancelled in between leaves a positive count with no rows
  // — and the rows are the durable truth. That case is handled where it shows
  // up: the table's empty state names exactly this ("these rows were not
  // saved"), instead of the old copy claiming a clean run and contradicting the
  // badge right above it.
  if (run.evaluateUnusedColumns && (run.coverage?.evidenceIncompleteCount ?? 0) > 0)
    availableTabs.push("evidence");
  if (run.evaluateTotalSpace && hasFootprint && hasSpaceBreakdown) availableTabs.push("bySpace");
  if (run.evaluateTotalSpace && hasFootprint) availableTabs.push("files");
  const effectiveTab: Tab = availableTabs.includes(tab) ? tab : (availableTabs[0] ?? "orphans");
  const footprintOnly = run.evaluateTotalSpace && !run.evaluateOrphans;

  const tabCount = (tb: Tab): number | undefined =>
    tb === "orphans"
      ? run.coverage?.orphanCount
      : tb === "columns"
        ? run.coverage?.unloadedColumnQvdCount
        : tb === "evidence"
          ? run.coverage?.evidenceIncompleteCount
          : tb === "bySpace"
            ? run.coverage?.footprintBySpace.length
            : run.coverage?.footprintFileCount;

  const tabContent = (tb: Tab): ReactNode => {
    switch (tb) {
      case "orphans":
        return (
          <OrphanQvdsTable
            runId={run.id}
            orphanCount={run.coverage?.orphanCount}
            reclaimableBytes={run.coverage?.reclaimableBytes}
            familyOnlyCount={run.coverage?.familyOnlyCount}
            familyOnlyBytes={run.coverage?.familyOnlyBytes}
            baseUrl={connection?.baseUrl}
          />
        );
      case "columns":
        return (
          <UnloadedColumnsTable
            runId={run.id}
            qvdCount={run.coverage?.unloadedColumnQvdCount}
            sizeUnavailableCount={run.coverage?.sizeUnavailableCount}
            baseUrl={connection?.baseUrl}
          />
        );
      case "evidence":
        return <EvidenceIncompleteTable runId={run.id} baseUrl={connection?.baseUrl} />;
      case "bySpace":
        // key: param-only navigation between runs does not remount, so without
        // it the drilled space/prefix leaks into the next run.
        return run.coverage ? (
          <QvdFootprintBySpace
            key={run.id}
            runId={run.id}
            coverage={run.coverage}
            active={isRunActive(run.status)}
          />
        ) : null;
      case "files":
        return run.coverage ? (
          <QvdFilesTable
            runId={run.id}
            spaceOptions={[...run.coverage.footprintBySpace.map((s) => s.spaceName)].sort()}
            totalCount={run.coverage.footprintFileCount}
          />
        ) : null;
    }
  };

  return (
    <PageShell
      header={
        <PageHeader
          title={connection?.name || t("title")}
          description={connection?.baseUrl}
          back={back}
          actions={
            <>
              <CompareQvdRunMenu run={run} />
              {/* Debug surface: the run's execution logs live behind this button
                  (a right-side drawer), not a result tab — they are diagnostics. */}
              <Button
                ref={logsButton}
                variant="outline"
                size="sm"
                onClick={() => setLogsOpen(true)}
              >
                <ScrollText />
                {t("logs.button")}
              </Button>
              <ActionMenu
                label={t("header.export")}
                trigger={
                  <Button variant="outline" size="sm">
                    <Download />
                    {t("header.export")}
                    <ChevronDown />
                  </Button>
                }
                items={[
                  {
                    id: "orphans-csv",
                    label: t("header.exportOrphansCsv"),
                    disabled: !run.coverage?.orphanCount,
                    onSelect: () => handleExport("orphans", "csv"),
                  },
                  {
                    id: "orphans-json",
                    label: t("header.exportOrphansJson"),
                    disabled: !run.coverage?.orphanCount,
                    onSelect: () => handleExport("orphans", "json"),
                  },
                  {
                    id: "columns-csv",
                    label: t("header.exportColumnsCsv"),
                    disabled: !run.coverage?.unloadedColumnQvdCount,
                    onSelect: () => handleExport("columns", "csv"),
                  },
                  {
                    id: "columns-json",
                    label: t("header.exportColumnsJson"),
                    disabled: !run.coverage?.unloadedColumnQvdCount,
                    onSelect: () => handleExport("columns", "json"),
                  },
                ]}
              />
            </>
          }
        />
      }
    >
      <PageContent>
        <div className="flex flex-col gap-5">
          <Card className="flex-row flex-wrap items-center gap-3 px-4 py-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-faint">
              {t("detail.scopeLabel")}
            </span>
            <QvdScopeBadges scope={run.scope} limit={8} />
          </Card>

          <QvdAnalysisStatusStrip
            run={run}
            onCancel={() =>
              cancelMutation.mutate(run.id, {
                onError: (err) => toast.error(getErrorMessage(err, t("toast.cancelFailed"))),
              })
            }
            cancelPending={cancelMutation.isPending}
          />

          {/* The orphan coverage banner + not-analyzable list are lineage signals —
              only meaningful when the orphan phase ran. A failure CAUSE is not a
              lineage signal though, and a run that died with the orphan phase off
              would otherwise explain itself nowhere, so an error opens the banner
              on its own. */}
          {(run.evaluateOrphans || !!run.error) && (
            <QvdCoverageBanner
              coverage={run.coverage}
              error={run.error}
              onResume={handleResume}
              resuming={resumeMutation.isPending}
              canResume={!isRunActive(run.status)}
            />
          )}

          {/* Size-phase failure alert — only when wasted-bytes measurement was
              requested (so a run that never sized never shows it). */}
          {run.computeColumnSizes && <QvdSizeCoverageBanner coverage={run.coverage} />}

          {run.coverage && (
            <Card className="px-5 py-3">
              <QvdKpiCells run={run} />
            </Card>
          )}

          {/* What the run could NOT see, next to the numbers it produced: apps
              whose reload log was inaccessible. Non-blocking — the results below
              are complete as far as this run could measure, and this says how far
              that was. Only rendered when some app was actually affected. */}
          {run.evaluateOrphans && <UnverifiedAppsNotice coverage={run.coverage} />}

          {/* Consumption-by-type sits directly under the KPIs and is always
              visible whenever the run measured total space. */}
          {hasFootprint && run.coverage && <QvdFootprintByType coverage={run.coverage} />}

          {run.evaluateOrphans && run.coverage && run.coverage.notAnalyzable.length > 0 && (
            <NotAnalyzableSection
              items={run.coverage.notAnalyzable}
              onRetry={handleReprocess}
              retrying={reprocessMutation.isPending}
              retriableCount={retriableCount}
              disabled={isRunActive(run.status)}
              baseUrl={connection?.baseUrl}
            />
          )}

          {footprintOnly && !isRunActive(run.status) && (
            <Alert
              tone="info"
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAnalyzeOrphans}
                  loading={startMutation.isPending}
                >
                  {t("footprint.ctaButton")}
                </Button>
              }
            >
              {t("footprint.ctaText")}
            </Alert>
          )}

          {availableTabs.length > 1 ? (
            <Tabs
              label={t("tabs.label")}
              value={effectiveTab}
              onValueChange={(next) => setTab(next as Tab)}
              items={availableTabs.map((tb) => {
                const count = tabCount(tb);
                return {
                  value: tb,
                  label: (
                    <span className="inline-flex items-center gap-1.5">
                      {t(`tabs.${tb}`)}
                      {count !== undefined && (
                        <span className="font-mono text-xs text-faint">{count}</span>
                      )}
                    </span>
                  ),
                  content: tabContent(tb),
                };
              })}
            />
          ) : (
            availableTabs.length === 1 && tabContent(availableTabs[0])
          )}
        </div>
      </PageContent>

      <QvdRunLogsDrawer
        open={logsOpen}
        onOpenChange={setLogsOpen}
        runId={run.id}
        active={isRunActive(run.status)}
        restoreFocusRef={logsButton}
      />
    </PageShell>
  );
}
